import { round, DERIVATIVE_PRICE_DECIMALS } from "services/helpers";

export const TERMINATION_MODE_ALL = "all";
export const TERMINATION_MODE_PARTIAL = "partial";

const CASH_TOTAL_DECIMALS = DERIVATIVE_PRICE_DECIMALS;

const parseAmount = (value) => {
  const parsed = parseFloat(String(value ?? 0).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const parseSettlementNumber = (value) => {
  if (value == null || value === "") return null;
  const parsed = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};

export const computeTerminationCashTotal = ({
  quantity,
  unitPrice,
  strikePrice,
  role = 2,
  optionRight = 2,
  derivativeType = 2,
} = {}) => {
  const qty = parseSettlementNumber(quantity);
  const parsedUnit = parseSettlementNumber(unitPrice);
  if (qty == null || parsedUnit == null) return "";
  // Old rows store the share price as a negative number. A price is never negative.
  const unit = Math.abs(parsedUnit);
  const strike = parseSettlementNumber(strikePrice);
  if (strike == null) return round(unit * qty, CASH_TOTAL_DECIMALS);
  // Same payoff as the server (options/payoff.py): the holder gets close minus
  // strike on a call or forward, strike minus close on a put. An option is never
  // worth less than zero, a forward can be. The seller gets the opposite sign.
  // Positive means the portfolio receives cash.
  const isForward = Number(derivativeType ?? 2) === 3;
  let perUnit = unit - strike;
  if (!isForward && Number(optionRight ?? 2) === 3) perUnit = -perUnit;
  if (!isForward) perUnit = Math.max(perUnit, 0);
  if (Number(role ?? 2) === 3) perUnit = -perUnit;
  return round(perUnit * qty, CASH_TOTAL_DECIMALS) || 0;
};

const settlementAmountIsZero = (value) => {
  const parsed = parseSettlementNumber(value);
  return parsed == null || parsed === 0;
};

// Older terminations stored the unit price in termination_fee and no cash total.
// The newer fields are a decimal zero, which is truthy as a string, so
// `termination_price || termination_fee` never falls through.
// The cash total is never taken from exercise_outcome: that is the profit after
// premium and costs, and the server would subtract them again from `receives`.
export const resolveLegacyTerminationSettlement = (data) => {
  const terminated = Number(data?.option_state) === 5;
  let unitPrice = data?.termination_price;
  if (terminated && settlementAmountIsZero(unitPrice) && !settlementAmountIsZero(data?.termination_fee)) {
    unitPrice = data.termination_fee;
  }

  let total = data?.receives;
  if (terminated && settlementAmountIsZero(total)) {
    if (!settlementAmountIsZero(unitPrice)) {
      const computed = computeTerminationCashTotal({
        quantity: data?.quantity,
        unitPrice,
        strikePrice: data?.strike_price,
        role: data?.role,
        optionRight: data?.option_right,
        derivativeType: data?.derivative_type,
      });
      if (computed !== "") {
        total = computed;
      }
    }
  }

  return {
    termination_price: unitPrice ?? 0,
    receives: total ?? 0,
  };
};

export const getContractQuantity = (option) => {
  return round(parseAmount(option?.quantity), 6);
};

const toInt = (value, fallback = 0) => {
  const parsed = parseInt(String(value ?? fallback).replace(",", "."), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getContractLotSize = (option) => {
  return Math.max(toInt(option?.lot_size, 0), 0);
};

export const getContractLotAmount = (option) => {
  return Math.max(toInt(option?.lot_amount, 0), 0);
};

const applyLotFields = (contract, option, lotSize, overrides = {}, lotAmountOverride) => {
  const nextLotSize = Math.max(toInt(lotSize, 0), 0);
  const contractLotAmount = getContractLotAmount(option);
  const lotAmount = lotAmountOverride == null
    ? contractLotAmount
    : Math.max(toInt(lotAmountOverride, 0), 0);
  const quantity = round(nextLotSize * lotAmount, 6);
  const originalQuantity = getContractLotSize(option) * contractLotAmount;
  const ratio = originalQuantity > 0 ? quantity / originalQuantity : 0;
  const premiumTotal = round(parseAmount(option?.premium_total) * ratio, DERIVATIVE_PRICE_DECIMALS);
  const premium = quantity > 0 ? round(premiumTotal / quantity, DERIVATIVE_PRICE_DECIMALS) : 0;
  const otherExpenses = round(parseAmount(option?.other_expenses) * ratio, DERIVATIVE_PRICE_DECIMALS);

  return {
    ...contract,
    ...overrides,
    lot_size: nextLotSize,
    lot_amount: lotAmount,
    quantity,
    premium_total: premiumTotal,
    premium,
    other_expenses: otherExpenses,
  };
};

export const updateTerminatedContractField = (contract, field, value) => {
  const next = { ...contract, [field]: value };
  // Forward P/L still uses per-unit termination_fee; keep it in sync with the unit price.
  if (field === "termination_price") {
    next.termination_fee = value;
  }
  if (field === "termination_price" || field === "quantity") {
    const total = computeTerminationCashTotal({
      quantity: next.quantity,
      unitPrice: next.termination_price,
      strikePrice: next.strike_price,
      role: next.role,
      optionRight: next.option_right,
      derivativeType: next.derivative_type,
    });
    if (total !== "") {
      next.receives = total;
    }
  }
  return next;
};

export const normalizeOptionLotFields = (payload) => {
  const lotSize = Math.max(toInt(payload?.lot_size, 1), 1);
  const lotAmount = Math.max(toInt(payload?.lot_amount, 0), 0);
  const quantity = round(lotSize * lotAmount, 6);

  return {
    ...payload,
    lot_size: lotSize,
    lot_amount: lotAmount,
    quantity,
  };
};

export const stripUiOnlyOptionFields = (payload) => {
  const next = { ...payload };
  delete next.isNewContractAfterTermination;
  delete next.isTerminatedFromOriginal;
  delete next.asset;
  delete next.stock_private_obj;
  delete next.parent_option_id;
  delete next.source_file;
  return next;
};

export const buildOptionApiPayload = (
  source,
  { portfolioId, parentOption, formatDateField, formatExerciseDate, includeId = true } = {}
) => {
  const base = parentOption || {};
  const merged = normalizeOptionLotFields(
    stripUiOnlyOptionFields({
      ...base,
      ...source,
      asset_id: source?.asset_id ?? base?.asset_id,
      asset_type: source?.asset_type ?? base?.asset_type,
      asset_name: source?.asset_name ?? base?.asset_name,
      owner_name: source?.owner_name ?? base?.owner_name,
      writer_name: source?.writer_name ?? base?.writer_name,
      derivative_type: source?.derivative_type ?? base?.derivative_type,
      option_right: source?.option_right ?? base?.option_right,
      role: source?.role ?? base?.role,
      portfolio: portfolioId,
      start_date: formatDateField?.(source?.start_date ?? base?.start_date),
      end_date: formatDateField?.(source?.end_date ?? base?.end_date),
      // Null is intentional for a new remaining contract; do not inherit the
      // terminated parent's option_state_date via `??`.
      option_state_date: formatDateField?.(
        source && Object.prototype.hasOwnProperty.call(source, "option_state_date")
          ? source.option_state_date
          : base?.option_state_date
      ),
      exercise_date:
        source?.exercise_date != null
          ? formatExerciseDate?.(source.exercise_date)
          : base?.exercise_date
          ? formatExerciseDate?.(base.exercise_date)
          : null,
    })
  );

  const assetType = Number(merged.asset_type);
  const assetId = merged.asset_id;
  if (assetType === 2 && assetId) {
    merged.stock = assetId;
  } else if (source?.stock_private_obj?.id) {
    merged.stock = source.stock_private_obj.id;
  }

  if (!includeId) {
    delete merged.id;
  }

  return merged;
};

export const buildTerminationContracts = (
  option,
  { terminationLotSize, terminationQuantity, eventDate, remainingTradeId } = {}
) => {
  const originalLotSize = getContractLotSize(option);
  const lotAmount = getContractLotAmount(option);
  const originalQuantity = originalLotSize * lotAmount;

  let terminatedLotSize;
  if (terminationLotSize != null && terminationLotSize !== "") {
    terminatedLotSize = toInt(terminationLotSize, 0);
  } else if (lotAmount > 0 && terminationQuantity != null) {
    terminatedLotSize = Math.round(parseAmount(terminationQuantity) / lotAmount);
  } else {
    terminatedLotSize = 0;
  }

  terminatedLotSize = Math.min(Math.max(terminatedLotSize, 0), originalLotSize);
  let remainingLotSize = originalLotSize - terminatedLotSize;
  // Contracts stored as lot_size 1 and lot_amount = quantity cannot be split on
  // lot size: any partial quantity rounds to the whole contract. Split the lot
  // count instead so the remaining contract is still created.
  const lotSizeWasExplicit = terminationLotSize != null && terminationLotSize !== "";
  const requestedTerminated = terminationQuantity != null && terminationQuantity !== ""
    ? parseAmount(terminationQuantity)
    : null;
  let terminatedLotAmount = lotAmount;
  let remainingLotAmount = null;
  if (
    !lotSizeWasExplicit &&
    remainingLotSize <= 0 &&
    originalLotSize > 0 &&
    lotAmount > 1 &&
    requestedTerminated > 0 &&
    requestedTerminated < originalQuantity
  ) {
    terminatedLotAmount = Math.min(
      Math.max(Math.round(requestedTerminated / originalLotSize), 1),
      lotAmount - 1
    );
    remainingLotAmount = lotAmount - terminatedLotAmount;
    terminatedLotSize = originalLotSize;
    remainingLotSize = originalLotSize;
  }

  const terminatedQuantity = terminatedLotSize * terminatedLotAmount;
  const remainingQuantity = remainingLotAmount == null
    ? remainingLotSize * lotAmount
    : originalLotSize * remainingLotAmount;
  const mode = remainingLotSize > 0 ? TERMINATION_MODE_PARTIAL : TERMINATION_MODE_ALL;

  const terminated = applyLotFields(
    { ...option },
    option,
    terminatedLotSize,
    {
      option_state: 5,
      option_state_date: eventDate || option?.option_state_date || new Date(),
      parent_option_id: option?.id,
      termination_fee: option?.termination_fee ?? 0,
      notes: option?.notes ?? "",
      isTerminatedFromOriginal: true,
    },
    remainingLotAmount == null ? undefined : terminatedLotAmount
  );

  const result = {
    mode,
    terminated,
    remaining: null,
    terminatedLotSize,
    remainingLotSize,
    terminatedQuantity,
    remainingQuantity,
  };

  if (remainingLotSize > 0) {
    result.remaining = applyLotFields(
      { ...option },
      option,
      remainingLotAmount == null ? remainingLotSize : originalLotSize,
      {
        id: null,
        trade_id: remainingTradeId ?? "",
        option_state: 2,
        option_state_date: null,
        start_date: eventDate || option?.start_date,
        parent_option_id: option?.id,
        isNewContractAfterTermination: true,
      },
      remainingLotAmount == null ? undefined : remainingLotAmount
    );
  }

  return result;
};

export const OPTION_STATE_OPEN = 2;
export const OPTION_STATE_EXERCISED = 3;
export const OPTION_STATE_TERMINATED = 5;

const settlementFields = (settlement = {}) => ({
  termination_price: settlement.termination_price ?? 0,
  receives: settlement.receives ?? 0,
  termination_fee: settlement.termination_fee ?? settlement.termination_price ?? 0,
});

// Partial termination keeps the original row's quantity. The original becomes
// Exercised, a Terminated child keeps the old reference, and a new Open child
// carries the remaining quantity. All three share one event date.
export const buildPartialTerminationRows = (
  option,
  {
    terminationLotSize,
    terminationQuantity,
    eventDate,
    remainingTradeId,
    settlement,
    remainingOverrides,
    portfolioId,
    formatDateField,
    formatExerciseDate,
  } = {}
) => {
  const sharedEventDate = eventDate || option?.option_state_date || new Date();
  const contracts = buildTerminationContracts(option, {
    terminationLotSize,
    terminationQuantity,
    eventDate: sharedEventDate,
    remainingTradeId,
  });
  const formattedEventDate = formatDateField
    ? formatDateField(sharedEventDate)
    : sharedEventDate;

  if (contracts.mode === TERMINATION_MODE_ALL) {
    return {
      mode: contracts.mode,
      eventDate: sharedEventDate,
      originalPatch: {
        option_state: OPTION_STATE_TERMINATED,
        option_state_date: formattedEventDate,
        ...settlementFields(settlement),
      },
      terminatedCreate: null,
      remainingCreate: null,
    };
  }

  const terminatedSource = {
    ...contracts.terminated,
    ...settlementFields(settlement),
    id: null,
    option_state: OPTION_STATE_TERMINATED,
    option_state_date: sharedEventDate,
    start_date: option?.start_date,
    trade_id: option?.trade_id,
  };
  const terminatedCreate = buildOptionApiPayload(terminatedSource, {
    portfolioId,
    parentOption: option,
    formatDateField,
    formatExerciseDate,
    includeId: false,
  });
  terminatedCreate.parent_option = option?.id;
  terminatedCreate.option_state = OPTION_STATE_TERMINATED;
  terminatedCreate.option_state_date = formattedEventDate;
  terminatedCreate.trade_id = option?.trade_id;
  delete terminatedCreate.id;

  const remainingSource = {
    ...contracts.remaining,
    ...(remainingOverrides || {}),
    id: null,
    option_state: OPTION_STATE_OPEN,
    option_state_date: null,
    start_date: sharedEventDate,
    trade_id: remainingTradeId ?? contracts.remaining?.trade_id ?? "",
    termination_price: 0,
    receives: 0,
    termination_fee: 0,
  };
  const remainingCreate = buildOptionApiPayload(remainingSource, {
    portfolioId,
    parentOption: option,
    formatDateField,
    formatExerciseDate,
    includeId: false,
  });
  remainingCreate.parent_option = option?.id;
  remainingCreate.option_state = OPTION_STATE_OPEN;
  remainingCreate.option_state_date = null;
  remainingCreate.start_date = formattedEventDate;
  remainingCreate.termination_price = 0;
  remainingCreate.receives = 0;
  remainingCreate.termination_fee = 0;
  delete remainingCreate.id;

  // The confirmation form can still carry the original full premium. Split it
  // by the quantity each saved row actually keeps.
  const applyProratedCosts = (target) => {
    const whole = getContractQuantity(option);
    const part = getContractQuantity(target);
    const ratio = whole > 0 ? part / whole : 0;
    target.premium_total = round(parseAmount(option?.premium_total) * ratio, DERIVATIVE_PRICE_DECIMALS);
    target.other_expenses = round(parseAmount(option?.other_expenses) * ratio, DERIVATIVE_PRICE_DECIMALS);
    target.premium = part > 0
      ? round(target.premium_total / part, DERIVATIVE_PRICE_DECIMALS)
      : 0;
  };
  applyProratedCosts(terminatedCreate);
  applyProratedCosts(remainingCreate);

  return {
    mode: contracts.mode,
    eventDate: sharedEventDate,
    originalPatch: {
      option_state: OPTION_STATE_EXERCISED,
      option_state_date: formattedEventDate,
    },
    terminatedCreate,
    remainingCreate,
  };
};
